import {
  Component,
  Output,
  EventEmitter,
  input,
  inject,
  effect,
  computed,
  signal
} from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import {
    EnterpriseReport,
  EnterpriseReportRow
} from 'src/app/models/enterpriseReport.model';
import { EnterpriseReportService } from 'src/app/services/enterpriseReport.service';
import { startWith } from 'rxjs';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { DecimalPipe, CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { ToPrecisionPipe } from 'src/app/pipes/to-precision.pipe';
import { DecimalFormattingDirective } from '../../../directives/decimal-formatting.directive';

@Component({
  selector: 'app-indicative-yields',
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('0.75s ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('0.25s ease-out', style({ opacity: 0 }))
      ])
    ])
  ],
  templateUrl: './indicative-yields.component.html',
  styleUrls: ['./indicative-yields.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DecimalPipe, ToPrecisionPipe, DecimalFormattingDirective]
})
export class IndicativeYieldsComponent {
  private enterpriseReportService = inject(EnterpriseReportService);
  private decPipe = inject(DecimalPipe);

  @Output() parentEventTransmitter = new EventEmitter();
  readonly id = input<string>('');
  readonly reportType? = input<"Property" | "Paddock">();

  enterpriseReport = signal([]);

  enterpriseSelect = new FormControl<EnterpriseReportRow | null>(null);

  indicativeYieldForm: FormGroup = new FormGroup({
    mappedWellSuited: new FormControl({ value: 10, disabled: true }, [
      Validators.required
    ]),
    mappedSuitable: new FormControl({ value: 20, disabled: true }, [
      Validators.minLength(0)
    ]),
    mappedModeratelySuitable: new FormControl({ value: 30, disabled: true }, [
      Validators.minLength(0)
    ]),
    mappedUnsuitable: new FormControl({ value: 10, disabled: true }, [
      Validators.minLength(0)
    ]),

    // https://dev.azure.com/eskmapping/Farm%20and%20Forest%20Mapper/_workitems/edit/145/
    estimatedWellSuited: new FormControl({ value: 20, disabled: false }, [
      Validators.required
    ]),
    estimatedSuitable: new FormControl({ value: 17, disabled: false }, [
      Validators.minLength(0)
    ]),
    estimatedModeratelySuitable: new FormControl(
      { value: 14, disabled: false },
      [Validators.minLength(0)]
    ),
    estimatedUnsuitable: new FormControl({ value: 0, disabled: false }, [
      Validators.minLength(0)
    ])
  });

  finalHarvestAgeControl = new FormControl({ value: 15, disabled: false }, [
    Validators.required
  ]);

  selectedCropSpecies = toSignal<EnterpriseReportRow | null>(
    this.enterpriseSelect.valueChanges.pipe(startWith(null))
  );

  finalHarvestAge = toSignal<number>(
    this.finalHarvestAgeControl.valueChanges.pipe(startWith(15))
  );

  // Reactive emission effect - automatically emits when any signal changes
  private emissionEffect = effect(() => {

    if (this.selectedCropSpecies()) {
      this.parentEventTransmitter.emit({
        stage: 4,
        values: {
          selectedCropSpecies: this.selectedCropSpecies().crop.name,
          mappedWellSuited: this.mappedWellSuited(),
          mappedSuitable: this.mappedSuitable(),
          mappedModeratelySuitable: this.mappedModeratelySuitable(),
          mappedUnsuitable: this.mappedUnsuitable(),
          estimatedWellSuited: this.estimatedWellSuited(),
          estimatedSuitable: this.estimatedSuitable(),
          estimatedModeratelySuitable: this.estimatedModeratelySuitable(),
          estimatedUnsuitable: this.estimatedUnsuitable(),
          totalArea: this.totalArea(),
          averageMAI: this.averageMAI(),
          harvestableVolume: this.harvestableVolume(),
          harvestableVolumePerHectare: this.harvestableVolumePerHectare()
        }
      });
    }
  });

  // Convert form control values to signals for reactivity
  mappedWellSuited = toSignal<number>(
    this.indicativeYieldForm
      .get('mappedWellSuited')!
      .valueChanges.pipe(startWith(10))
  );
  mappedSuitable = toSignal<number>(
    this.indicativeYieldForm
      .get('mappedSuitable')!
      .valueChanges.pipe(startWith(20))
  );
  mappedModeratelySuitable = toSignal<number>(
    this.indicativeYieldForm
      .get('mappedModeratelySuitable')!
      .valueChanges.pipe(startWith(30))
  );
  mappedUnsuitable = toSignal<number>(
    this.indicativeYieldForm
      .get('mappedUnsuitable')!
      .valueChanges.pipe(startWith(10))
  );

  estimatedWellSuited = toSignal<number>(
    this.indicativeYieldForm
      .get('estimatedWellSuited')!
      .valueChanges.pipe(startWith(20))
  );
  estimatedSuitable = toSignal<number>(
    this.indicativeYieldForm
      .get('estimatedSuitable')!
      .valueChanges.pipe(startWith(17))
  );
  estimatedModeratelySuitable = toSignal<number>(
    this.indicativeYieldForm
      .get('estimatedModeratelySuitable')!
      .valueChanges.pipe(startWith(14))
  );
  estimatedUnsuitable = toSignal<number>(
    this.indicativeYieldForm
      .get('estimatedUnsuitable')!
      .valueChanges.pipe(startWith(0))
  );

  totalArea = computed(() => {
    const selectedCropSpecies = this.selectedCropSpecies();

    if (selectedCropSpecies == null) return 0;

    const totalArea =
      this.mappedWellSuited() +
      this.mappedSuitable() +
      this.mappedModeratelySuitable() +
      this.mappedUnsuitable();
    return totalArea;
  });

  averageMAI = computed(() => {
    const totalArea = this.totalArea();

    const wellSuited: number =
      this.mappedWellSuited() * this.estimatedWellSuited();
    const suitable: number = this.mappedSuitable() * this.estimatedSuitable();
    const moderatelySuitable: number =
      this.mappedModeratelySuitable() * this.estimatedModeratelySuitable();
    const unsuited: number =
      this.mappedUnsuitable() * this.estimatedUnsuitable();

    return this.toPrecision(
      (wellSuited + suitable + moderatelySuitable + unsuited) / totalArea
    );
  });

  harvestableVolume = computed(() => {
    const totalArea = this.totalArea();
    const averageMAI = this.averageMAI();
    const finalHarvestAge = this.finalHarvestAge();

    return this.toPrecision(averageMAI * finalHarvestAge * totalArea);
  });

  harvestableVolumePerHectare = computed(() => {
    const harvestableVolume = this.harvestableVolume();
    const totalArea = this.totalArea();
    return this.toPrecision(harvestableVolume / totalArea);
  });

  constructor() {
    effect(() => {
      const selectedCropSpecies = this.selectedCropSpecies();

      if (selectedCropSpecies == null) return;

      const { crop, wellSuited, suitable, unsuitable, moderatelySuitable } =
        selectedCropSpecies;

      if (crop.label === 'Radiata Pine') {
        this.finalHarvestAgeControl.setValue(30);
      } else {
        this.finalHarvestAgeControl.setValue(15);
      }

      this.indicativeYieldForm
        .get('mappedWellSuited')
        .setValue(this.toPrecision(wellSuited));
      this.indicativeYieldForm
        .get('mappedSuitable')
        .setValue(this.toPrecision(suitable));
      this.indicativeYieldForm
        .get('mappedModeratelySuitable')
        .setValue(this.toPrecision(moderatelySuitable));
      this.indicativeYieldForm
        .get('mappedUnsuitable')
        .setValue(this.toPrecision(unsuitable));
    });

    effect(async () => {
      const id = this.id();

      if (id == null || id === '') return [];

      let report: EnterpriseReport;
      if (!this.reportType()) {
        return []
      }
      if (this.reportType() === "Property" ) {
        report = await this.enterpriseReportService.getPropertyReport(id);
      }
      else {
        report = await this.enterpriseReportService.getPaddockReport(id);
      }

      if (report == null) return [];

      const { data } = report;
      console.log(data)

      const filteredData = data.filter(
        (value) =>
          value.crop.label.toLowerCase().includes('pine') ||
          value.crop.label.toLowerCase().includes('eucalyptus')
      );

      this.enterpriseReport.set(filteredData);
    });
  }

  toPrecision(x) {
    return Number(x.toPrecision(5));
  }
}
